import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import axios from 'axios';
import { URLApi } from '../../../services/api_function';

const NotificationComponent = () => {
  // State for sending notifications
  const [message, setMessage] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState(null);
  
  // State for message history
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState('');
  const [activeTab, setActiveTab] = useState('send'); // 'send' or 'history'
  const [filterRole, setFilterRole] = useState('all');

  const roles = [
    { id: 1, name: 'Advocate', description: 'Community advocates', icon: 'bi-megaphone', color: '#10b981' },
    { id: 2, name: 'DAO', description: 'Platform administrators', icon: 'bi-shield-check', color: '#8b5cf6' },
    { id: 3, name: 'Delegator', description: 'Token delegation participants', icon: 'bi-arrow-left-right', color: '#f59e0b' },
    { id: 4, name: 'All Users', description: 'Send to everyone', icon: 'bi-people', color: '#3b82f6' }
  ];

  // Mock API functions
  const sendNotificationAPI = async (message, role) => {
    console.log('Sending notification:', { message, role });
    const data = await axios.post(`${URLApi}/sendMessage`, { message, type: role });
    return data;
  };

  const fetchMessagesAPI = async () => {
    const response = await axios.get(`${URLApi}/getAllMessages`);
    console.log('Fetched messages:', response.data);
    return response.data;
  };

 const updateMessageAPI = async (id, newMessage) => {
  try {
    console.log("Updating message:", id, newMessage);

    const res = await axios.put(
      `${URLApi}/updateAdminMessage/${id}`,
      { message: newMessage }
    );

    return res.data; // { success: true, message: ... }
  } catch (error) {
    console.error("Update message error:", error);
    throw error;
  }
};

const deleteMessageAPI = async (id) => {
  try {
    console.log("Deleting message:", id);

    const response = await axios.delete(
      `${URLApi}/deleteAdminMessage/${id}`
    );

    return response.data; // { success: true, message: ... }
  } catch (error) {
    console.error("Delete message error:", error);
    throw error;
  }
};

  // Fetch messages on component mount
  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await fetchMessagesAPI();
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setStatus({ type: 'error', text: 'Failed to load message history' });
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!message.trim()) {
      setStatus({ type: 'error', text: 'Please enter a message' });
      return;
    }

    if (!selectedRole) {
      setStatus({ type: 'error', text: 'Please select a recipient role' });
      return;
    }

    setIsSending(true);
    setStatus(null);

    try {
      await sendNotificationAPI(message, selectedRole);
      
      // Add the new message to the history
      const newMessage = {
        id: messages.length + 1,
        message: message,
        role: roles.find(r => r.id === selectedRole)?.name,
        roleId: selectedRole,
        timestamp: new Date().toISOString(),
        sentBy: 'You',
        status: 'sent'
      };
      
      setMessages([newMessage, ...messages]);
      setStatus({ 
        type: 'success', 
        text: `Notification sent successfully to ${roles.find(r => r.id === selectedRole)?.name}!` 
      });
      setMessage('');
      setSelectedRole('');
      
      setTimeout(() => setStatus(null), 3000);
      
    } catch (error) {
      setStatus({ type: 'error', text: 'Failed to send notification. Please try again.' });
    } finally {
      setIsSending(false);
    }
  };

  const handleEdit = (message) => {
    setEditingMessage(message._id);
    setEditText(message.message);
  };

  const handleSaveEdit = async (id) => {
    if (!editText.trim()) {
      setStatus({ type: 'error', text: 'Message cannot be empty' });
      return;
    }

    try {
      await updateMessageAPI(id, editText);
      setMessages(messages.map(msg => 
        msg.id === id ? { ...msg, message: editText } : msg
      ));
      setEditingMessage(null);
      setStatus({ type: 'success', text: 'Message updated successfully' });
      setTimeout(() => setStatus(null), 3000);
      fetchMessages(); // Refresh messages to get updated timestamp
    } catch (error) {
      setStatus({ type: 'error', text: 'Failed to update message' });
    }
  };

const handleDelete = async (id) => {
  if (!window.confirm("Are you sure you want to delete this message?")) return;

  try {
    await deleteMessageAPI(id);

    setMessages((prev) => prev.filter((msg) => msg._id !== id));

    setStatus({ type: "success", text: "Message deleted successfully" });
    setTimeout(() => setStatus(null), 3000);
  } catch (error) {
    console.error(error);
    setStatus({ type: "error", text: "Failed to delete message" });
  }
};


  const handleCancelEdit = () => {
    setEditingMessage(null);
    setEditText('');
  };

  const filteredMessages = filterRole === 'all' 
    ? messages 
    : messages.filter(msg => msg.roleId === parseInt(filterRole));

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleColor = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.color : '#6b7280';
  };

  return (
    <div className="container-fluid py-4" style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <div className="row justify-content-center">
        <div className="col-12 col-xl-10">
          
          {/* Header Section */}
          <div className="text-center mb-5">
            <div className="d-inline-flex align-items-center justify-content-center mb-3">
              <div style={{
                width: '56px',
                height: '56px',
                background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(67, 97, 238, 0.2)'
              }}>
                <i className="bi bi-bell text-white fs-4"></i>
              </div>
            </div>
            <h2 className="fw-bold mb-2" style={{ color: '#2d3748' }}>Notification Center</h2>
            <p className="text-muted mb-0">Send notifications and manage message history</p>
          </div>

          {/* Tab Navigation */}
          <div className="mb-4">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
              <div className="card-body p-2">
                <div className="row g-2">
                  <div className="col-6">
                    <button
                      className={`btn w-100 py-3 fw-semibold ${activeTab === 'send' ? 'btn-primary' : 'btn-light'}`}
                      onClick={() => setActiveTab('send')}
                      style={{ borderRadius: '10px' }}
                    >
                      <i className="bi bi-send me-2"></i>
                      Send Notification
                    </button>
                  </div>
                  <div className="col-6">
                    <button
                      className={`btn w-100 py-3 fw-semibold ${activeTab === 'history' ? 'btn-primary' : 'btn-light'}`}
                      onClick={() => setActiveTab('history')}
                      style={{ borderRadius: '10px' }}
                    >
                      <i className="bi bi-clock-history me-2"></i>
                      Message History
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Status Message */}
          {status && (
            <div className="mb-4">
              <div className={`alert ${status.type === 'success' ? 'alert-success' : 'alert-danger'} border-0 shadow-sm d-flex align-items-center`}
                   style={{ borderRadius: '12px' }}>
                <i className={`bi ${status.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'} me-2`}></i>
                <span className="flex-grow-1">{status.text}</span>
                <button 
                  className="btn-close" 
                  onClick={() => setStatus(null)}
                  aria-label="Close"
                ></button>
              </div>
            </div>
          )}

          {/* Send Notification Tab */}
          {activeTab === 'send' && (
            <div className="card border-0 shadow-lg" style={{ borderRadius: '16px' }}>
              <div className="card-body p-4 p-md-5">
                
                {/* Message Input */}
                <div className="mb-5">
                  <label className="form-label fw-semibold mb-3 d-flex align-items-center" style={{ color: '#4a5568' }}>
                    <i className="bi bi-chat-left-text me-2"></i>
                    Message Content
                  </label>
                  <div className="position-relative">
                    <textarea
                      className="form-control p-3"
                      rows="4"
                      placeholder="Write your notification message here..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      maxLength="500"
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        fontSize: '16px',
                        resize: 'none',
                        transition: 'all 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#4361ee'}
                      onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                    />
                    <div className="position-absolute bottom-0 end-0 p-3">
                      <span className={`small ${message.length === 500 ? 'text-danger' : 'text-muted'}`}>
                        {message.length}/500
                      </span>
                    </div>
                  </div>
                  <small className="text-muted mt-2 d-block">
                    Keep messages clear and concise for better engagement
                  </small>
                </div>

                {/* Role Selection */}
                <div className="mb-5">
                  <label className="form-label fw-semibold mb-3 d-flex align-items-center" style={{ color: '#4a5568' }}>
                    <i className="bi bi-people me-2"></i>
                    Select Recipients
                  </label>
                  <div className="row g-3">
                    {roles.map(role => (
                      <div key={role.id} className="col-12 col-sm-6">
                        <div
                          className={`p-3 rounded-3 border cursor-pointer ${selectedRole === role.id ? 'border-primary shadow-sm' : 'border'} ${selectedRole === role.id ? 'bg-primary bg-opacity-10' : 'bg-white'}`}
                          onClick={() => setSelectedRole(role.id)}
                          style={{
                            transition: 'all 0.2s',
                            cursor: 'pointer',
                            minHeight: '100px'
                          }}
                        >
                          <div className="d-flex align-items-start">
                            <div className={`rounded-circle d-flex align-items-center justify-content-center me-3`}
                                 style={{ 
                                   width: '40px', 
                                   height: '40px',
                                   backgroundColor: selectedRole === role.id ? role.color : `${role.color}20`
                                 }}>
                              <i className={`bi ${role.icon} ${selectedRole === role.id ? 'text-white' : ''}`}
                                 style={{ color: selectedRole === role.id ? '#fff' : role.color }}></i>
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="fw-semibold mb-1" style={{ color: selectedRole === role.id ? '#4361ee' : '#2d3748' }}>
                                {role.name}
                              </h6>
                              <small className="text-muted">{role.description}</small>
                            </div>
                            {selectedRole === role.id && (
                              <div className="ms-auto">
                                <i className="bi bi-check-circle-fill" style={{ color: role.color }}></i>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Button */}
                <div className="d-grid">
                  <button
                    className="btn btn-lg fw-semibold py-3"
                    onClick={handleSend}
                    disabled={isSending || !message.trim() || !selectedRole}
                    style={{
                      background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '16px',
                      transition: 'all 0.2s',
                      opacity: isSending || !message.trim() || !selectedRole ? 0.6 : 1
                    }}
                  >
                    {isSending ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Sending Notification...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send me-2"></i>
                        Send Notification
                      </>
                    )}
                  </button>
                </div>

                {/* Preview Footer */}
                <div className="text-center mt-4 pt-3 border-top">
                  <small className="text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    Ready to send to{' '}
                    <strong className="text-primary">
                      {selectedRole ? roles.find(r => r.id === selectedRole)?.name : 'No role selected'}
                    </strong>
                  </small>
                </div>

              </div>
            </div>
          )}

          {/* Message History Tab */}
          {activeTab === 'history' && (
            <div className="card border-0 shadow-lg" style={{ borderRadius: '16px' }}>
              <div className="card-body p-4 p-md-5">
                
                {/* Header with Filter */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
                  <div>
                    <h5 className="fw-bold mb-1" style={{ color: '#2d3748' }}>
                      <i className="bi bi-clock-history me-2"></i>
                      Notification History
                    </h5>
                    <small className="text-muted">
                      {filteredMessages.length} message{filteredMessages.length !== 1 ? 's' : ''} found
                    </small>
                  </div>
                  
                  <div className="mt-3 mt-md-0">
                    <select 
                      className="form-select"
                      value={filterRole}
                      onChange={(e) => setFilterRole(e.target.value)}
                      style={{ 
                        borderRadius: '10px',
                        borderColor: '#e2e8f0',
                        width: '180px'
                      }}
                    >
                      <option value="all">All Roles</option>
                      {roles.map(role => (
                        <option key={role.id} value={role.id}>{role.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Messages List */}
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-3">Loading messages...</p>
                  </div>
                ) : filteredMessages.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-3">
                      <i className="bi bi-inbox fs-1 text-muted"></i>
                    </div>
                    <h6 className="text-muted mb-2">No messages found</h6>
                    <small className="text-muted">
                      {filterRole === 'all' 
                        ? 'No notification history available'
                        : `No messages for ${roles.find(r => r.id === parseInt(filterRole))?.name}`
                      }
                    </small>
                  </div>
                ) : (
                  <div className="list-group" style={{ gap: '12px' }}>
                    {filteredMessages.map(msg => (
                      <div 
                        key={msg.id}
                        className="list-group-item border-0 p-4 shadow-sm"
                        style={{ 
                          borderRadius: '14px',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3">
                          <div className="d-flex align-items-center mb-2 mb-md-0">
                            <div 
                              className="rounded-circle d-flex align-items-center justify-content-center me-3"
                              style={{
                                width: '40px',
                                height: '40px',
                                backgroundColor: `${getRoleColor(msg.roleId)}20`
                              }}
                            >
                              <i 
                                className={`bi ${roles.find(r => r.id === msg.roleId)?.icon || 'bi-bell'}`}
                                style={{ color: getRoleColor(msg.roleId) }}
                              ></i>
                            </div>
                            <div>
                              <span 
                                className="badge mb-1"
                                style={{ 
                                  backgroundColor: `${getRoleColor(msg.roleId)}20`,
                                  color: getRoleColor(msg.roleId),
                                  fontWeight: '500'
                                }}
                              >
                                {msg.role}
                              </span>
                              <div className="small text-muted">
                                Sent by {msg.sentBy} • {formatDate(msg.timestamp)}
                              </div>
                            </div>
                          </div>
                          
                          <div className="d-flex gap-2">
                            {editingMessage === msg._id ? (
                              <>
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() => handleSaveEdit(msg._id)}
                                >
                                  <i className="bi bi-check"></i> Save
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={handleCancelEdit}
                                >
                                  <i className="bi bi-x"></i> Cancel
                                </button>
                              </>
                            ) : (
                              <>
                               <button
  onClick={() => handleEdit(msg)}
  title="Edit message"
  className="btn btn-sm d-flex align-items-center justify-content-center"
  style={{
    borderRadius: "8px",
    border: "1px solid #0d6efd",
    background: "rgba(13,110,253,0.1)",
    color: "#0d6efd",
    width: "34px",
    height: "34px",
  }}
>
  <i className="bi bi-pencil"></i>
</button>

                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(msg._id)}
                                  title="Delete message"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {editingMessage === msg._id ? (
                          <textarea
                            className="form-control"
                            rows="2"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            style={{
                              borderColor: '#4361ee',
                              borderRadius: '8px'
                            }}
                          />
                        ) : (
                          <p className="mb-0" style={{ color: '#4a5568', lineHeight: '1.6' }}>
                            {msg.message}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* History Stats */}
                {!loading && filteredMessages.length > 0 && (
                  <div className="mt-4 pt-3 border-top">
                    <div className="row text-center">
                      <div className="col-4">
                        <div className="fw-bold" style={{ color: '#4361ee' }}>
                          {messages.length}
                        </div>
                        <small className="text-muted">Total Messages</small>
                      </div>
                      <div className="col-4">
                        <div className="fw-bold" style={{ color: '#10b981' }}>
                          {messages.filter(m => m.status === 'sent').length}
                        </div>
                        <small className="text-muted">Successful</small>
                      </div>
                      <div className="col-4">
                        <div className="fw-bold" style={{ color: '#f59e0b' }}>
                          {new Set(messages.map(m => m.sentBy)).size}
                        </div>
                        <small className="text-muted">Senders</small>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          )}

          {/* Additional Info */}
          <div className="text-center mt-4">
            <small className="text-muted">
              <i className="bi bi-clock-history me-1"></i>
              {activeTab === 'send' 
                ? 'Notifications are delivered instantly to online users'
                : 'Message history is stored for 90 days'
              }
            </small>
          </div>

        </div>
      </div>
    </div>
  );
};

export default NotificationComponent;